import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, AlertCircle, Clock } from "lucide-react";
import CleaningDashboard from "./CleaningDashboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CleaningViewAs() {
  // Token impersonato in-memory (NON salvato in localStorage)
  const [impersonateToken, setImpersonateToken] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(true);
  const [tokenError, setTokenError] = useState(null);
  const [tokenExpiry, setTokenExpiry] = useState(null);
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [renewingToken, setRenewingToken] = useState(false);

  // Ottieni token admin da localStorage per richiedere l'impersonazione
  const getAdminToken = () => {
    // Prima prova con la nuova chiave "admin_token"
    const adminToken = localStorage.getItem("admin_token");
    if (adminToken) return adminToken;
    
    // Fallback alla vecchia chiave "token" per retrocompatibilità
    return localStorage.getItem("token");
  };

  // Funzione per richiedere token di impersonazione
  const fetchImpersonateToken = useCallback(async (isRenewal = false) => {
    const adminToken = getAdminToken();
    
    if (!adminToken) {
      setTokenError("Sessione admin non trovata. Effettua il login come admin.");
      setTokenLoading(false);
      return false;
    }

    try {
      if (isRenewal) {
        setRenewingToken(true);
      }
      
      const response = await axios.post(
        `${API}/admin/impersonate`,
        { target_role: "cleaning" },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      setImpersonateToken(response.data.token);
      setTokenExpiry(Date.now() + response.data.expires_in_minutes * 60 * 1000);
      setTimeRemaining(response.data.expires_in_minutes * 60);
      setTokenError(null);
      setIsTokenExpired(false);
      
      if (isRenewal) {
        toast.success("Sessione rinnovata con successo");
      } else {
        toast.success("Visualizzazione come utente Pulizie attiva");
      }
      return true;
    } catch (error) {
      console.error("Errore impersonazione:", error);
      if (error.response?.status === 401) {
        setTokenError("Sessione admin scaduta. Effettua nuovamente il login nella dashboard admin.");
      } else if (error.response?.status === 403) {
        setTokenError("Solo gli admin possono usare questa funzione.");
      } else {
        setTokenError("Errore durante l'impersonazione. Riprova.");
      }
      return false;
    } finally {
      setTokenLoading(false);
      setRenewingToken(false);
    }
  }, []);

  // Richiedi token di impersonazione all'avvio
  useEffect(() => {
    fetchImpersonateToken(false);
  }, [fetchImpersonateToken]);

  // Countdown timer
  useEffect(() => {
    if (!tokenExpiry || isTokenExpired) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((tokenExpiry - now) / 1000));
      setTimeRemaining(remaining);

      if (remaining <= 0) {
        setIsTokenExpired(true);
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [tokenExpiry, isTokenExpired]);

  // Formatta tempo rimanente
  const formatTimeRemaining = (seconds) => {
    if (seconds === null) return "";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Determina colore countdown
  const getCountdownColor = () => {
    if (timeRemaining === null) return "text-white/75";
    if (timeRemaining <= 60) return "text-red-200 font-bold animate-pulse";
    if (timeRemaining <= 300) return "text-yellow-200 font-semibold";
    return "text-white/75";
  };

  // Rinnova token
  const handleRenewToken = async () => {
    await fetchImpersonateToken(true);
  };

  // Callback quando CleaningDashboard rileva token scaduto
  const handleTokenExpired = useCallback(() => {
    setIsTokenExpired(true);
  }, []);

  // Loading token
  if (tokenLoading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="animate-spin text-[#D4AF37] mx-auto mb-4" size={48} />
          <p className="text-[#78716C]">Inizializzazione visualizzazione Pulizie...</p>
        </div>
      </div>
    );
  }

  // Errore token (sessione admin non trovata o altro errore)
  if (tokenError && !isTokenExpired) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <div className="text-center max-w-md p-8 bg-white rounded-lg shadow-lg">
          <AlertCircle className="text-red-500 mx-auto mb-4" size={48} />
          <h2 className="text-xl font-bold text-[#1C1917] mb-2">Accesso non riuscito</h2>
          <p className="text-[#78716C] mb-6">{tokenError}</p>
          <button 
            onClick={() => window.close()}
            className="px-6 py-2 bg-[#1C1917] text-white rounded-lg hover:bg-[#44403C] transition-colors"
          >
            Chiudi questa scheda
          </button>
        </div>
      </div>
    );
  }

  // Token scaduto - mostra messaggio con opzione rinnovo
  if (isTokenExpired) {
    return (
      <div className="min-h-screen bg-[#F9F8F6]">
        {/* Banner scadenza */}
        <div className="bg-red-500 text-white px-4 py-2 text-center text-sm">
          <div className="flex items-center justify-center gap-2">
            <Clock size={16} />
            <span><strong>Sessione scaduta</strong></span>
          </div>
        </div>
        
        <div className="flex items-center justify-center min-h-[calc(100vh-40px)]">
          <div className="text-center max-w-md p-8 bg-white rounded-lg shadow-lg">
            <Clock className="text-amber-500 mx-auto mb-4" size={48} />
            <h2 className="text-xl font-bold text-[#1C1917] mb-2">Sessione di visualizzazione scaduta</h2>
            <p className="text-[#78716C] mb-6">
              La sessione di visualizzazione Pulizie è scaduta dopo 15 minuti di inattività.
              <br /><br />
              Puoi rinnovare la sessione cliccando "Rinnova" oppure riaprire | Cleaning | dalla dashboard admin.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button 
                onClick={handleRenewToken}
                disabled={renewingToken}
                className="px-6 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B8972E] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {renewingToken ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Rinnovo in corso...
                  </>
                ) : (
                  <>
                    <RefreshCw size={16} />
                    Rinnova sessione
                  </>
                )}
              </button>
              <button 
                onClick={() => window.close()}
                className="px-6 py-2 bg-[#1C1917] text-white rounded-lg hover:bg-[#44403C] transition-colors"
              >
                Chiudi scheda
              </button>
            </div>
            {tokenError && (
              <p className="text-red-500 text-sm mt-4">{tokenError}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Render CleaningDashboard with impersonation token
  return (
    <CleaningDashboard 
      impersonateToken={impersonateToken} 
      tokenExpiry={tokenExpiry}
      timeRemaining={timeRemaining}
      formatTimeRemaining={formatTimeRemaining}
      getCountdownColor={getCountdownColor}
      onTokenExpired={handleTokenExpired}
      onRenewToken={handleRenewToken}
      renewingToken={renewingToken}
    />
  );
}
